import {
  getCatalogCarousels,
  getCarousels,
  getCategory1,
  getCollection1,
  getProduct1,
  getProductDiscounts,
  getProductByHandle,
  listCategories1,
  listCollections1,
  listRegions1,
  listReviews,
  listSitemapProducts,
} from "@/generated/api/sdk.gen"
import type { ListCategories1Data, ListCollections1Data, ListReviewsData, ListSitemapProductsData } from "@/generated/api/types.gen"
import type { Api, RequestPolicy } from "@/platform/api/api"
import { body } from "@/platform/api/gateways/common"

type Query<T extends { query?: unknown }> = NonNullable<T["query"]>

type ProductLookup = ({ id: string; handle?: never } | { handle: string; id?: never }) & {
  regionId?: string
  publicOnly?: boolean
}

const publicRead = (read?: RequestPolicy): RequestPolicy => ({ ...read, credentials: "public" })

export const catalogGateway = (api: Api) => ({
  carousels: (productId?: string) =>
    productId ? body(getCarousels({ ...api.request({ credentials: "public" }), path: { productId } })) : body(getCatalogCarousels(api.request({ credentials: "public" }))),
  collections: (query: Query<ListCollections1Data>, read?: RequestPolicy) => body(listCollections1({ ...api.request(publicRead(read)), query })),

  collection: (id: string, read?: RequestPolicy) => body(getCollection1({ ...api.request(publicRead(read)), path: { id } })),

  categories: (query: Query<ListCategories1Data>, read?: RequestPolicy) => body(listCategories1({ ...api.request(publicRead(read)), query })),

  category: (id: string, read?: RequestPolicy) => body(getCategory1({ ...api.request(publicRead(read)), path: { id } })),

  regions: (read?: RequestPolicy) => body(listRegions1(api.request(publicRead(read)))),

  reviews: (productId: string, query: Query<ListReviewsData>) => body(listReviews({ ...api.request({ credentials: "public" }), path: { id: productId }, query })),

  discounts: async (productIds: string[], publicOnly = false) => {
    const response = await body(getProductDiscounts({ ...api.request(publicOnly ? { credentials: "public" } : {}), body: { productIds } }))
    return response.productsDiscounts.flatMap((product) => product.discounts)
  },

  sitemapProducts: (query: Query<ListSitemapProductsData>, read?: RequestPolicy) => body(listSitemapProducts({ ...api.request(publicRead(read)), query })),

  product: async ({ id, regionId, handle, publicOnly = false }: ProductLookup) => {
    const policy: RequestPolicy = { intent: "dynamic", next: { revalidate: 0 }, ...(publicOnly ? { credentials: "public" } : {}) }
    const query = regionId !== undefined ? { region_id: regionId } : {}
    const response =
      handle !== undefined
        ? await body(getProductByHandle({ ...api.request(policy), path: { handle }, query }))
        : await body(getProduct1({ ...api.request(policy), path: { id }, query }))
    return response.product
  },
})

export type CatalogGateway = ReturnType<typeof catalogGateway>
